import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { db } from "@/lib/db";
import type { Document, Occurrence } from "@/types";

interface SenderGroup {
  email: string;
  name: string;
  count: number;
  documents: (Document & { occurrence: Occurrence })[];
}

export default function Senders() {
  const [senders, setSenders] = useState<SenderGroup[]>([]);
  const [selectedSender, setSelectedSender] = useState<string | null>(null);

  useEffect(() => {
    db.occurrences.toArray().then(async (occurrences) => {
      const grouped = new Map<string, SenderGroup>();
      for (const occ of occurrences) {
        const email = occ.senderEmail || "Unknown";
        if (!grouped.has(email)) {
          grouped.set(email, { email, name: occ.senderName || email, count: 0, documents: [] });
        }
        const group = grouped.get(email)!;
        const doc = await db.documents.get(occ.documentId);
        if (doc) {
          group.documents.push({ ...doc, occurrence: occ });
          group.count++;
        }
      }
      setSenders(Array.from(grouped.values()).sort((a, b) => b.count - a.count));
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Senders ({senders.length})</h3>

        {senders.length === 0 ? (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-2 dark:bg-dark-surface-2 mb-3">
              <Users size={24} className="text-text-muted" />
            </div>
            <p className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">No senders yet</p>
            <p className="mt-1 text-xs text-text-muted">Sync your email to see sender information</p>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {senders.map((sender) => (
              <div
                key={sender.email}
                onClick={() => setSelectedSender(sender.email === selectedSender ? null : sender.email)}
                className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                  selectedSender === sender.email
                    ? "border-accent bg-accent/5 dark:border-accent"
                    : "border-border-default hover:bg-surface-2 dark:border-dark-border-default dark:hover:bg-dark-surface-2"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">{sender.name}</p>
                    <p className="text-xs text-text-muted dark:text-dark-text-muted">{sender.email}</p>
                  </div>
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-text-secondary dark:bg-dark-surface-2 dark:text-dark-text-secondary">
                    {sender.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedSender && (
        <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Documents from sender</h3>
          <div className="mt-3 space-y-2">
            {senders.find((s) => s.email === selectedSender)?.documents.map((d) => (
              <div key={d.id} className="text-sm text-text-secondary dark:text-dark-text-secondary">
                {d.canonicalName} · {d.occurrence.subject}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
